import React, {createContext, useCallback, useContext, useMemo, useState} from "react";

/**
 * Shared signal used to coordinate reloads between webmail components.
 *
 * Components that mutate mailbox state (e.g. deleting or marking messages)
 * call {@link notifyReload} to broadcast that other components should
 * refresh their data. Components that display derived mailbox data
 * (e.g. quota, unread counts) include {@link reloadCounter} in their
 * {@code useEffect} dependency array to trigger a re-fetch whenever a
 * mutation occurs.
 */
interface IWebmailReloadContext {
    /**
     * Monotonically increasing counter incremented by each call to
     * {@link notifyReload}. Include this value in a {@code useEffect}
     * dependency array to re-run the effect after any webmail mutation.
     */
    reloadCounter: number;
    /**
     * Signals that a webmail mutation has occurred and that components
     * displaying derived mailbox data should reload. Increments
     * {@link reloadCounter} by one.
     */
    notifyReload: () => void;
}

const WebmailReloadContext = createContext<IWebmailReloadContext | undefined>(undefined);

interface WebmailReloadContextProviderProps {
    children: React.ReactNode;
}

/**
 * Provides {@link IWebmailReloadContext} to the webmail component tree.
 * Should be mounted once at the root of the webmail section of the portal.
 */
export function WebmailReloadContextProvider({children}: Readonly<WebmailReloadContextProviderProps>)
{
    const [reloadCounter, setReloadCounter] = useState(0);
    const notifyReload = useCallback(() => setReloadCounter(c => c + 1), []);

    const exposedData = useMemo<IWebmailReloadContext>(() => ({
        reloadCounter,
        notifyReload,
    }), [reloadCounter, notifyReload]);

    return (
        <WebmailReloadContext.Provider value={exposedData}>
            {children}
        </WebmailReloadContext.Provider>
    );
}

/**
 * Returns the {@link IWebmailReloadContext} for the nearest
 * {@link WebmailReloadContextProvider}.
 *
 * Must be used within the scope of {@link WebmailReloadContextProvider}.
 */
export function useWebmailReloadContext(): IWebmailReloadContext
{
    const context = useContext(WebmailReloadContext);

    if (!context) {
        throw new Error(
            "useWebmailReloadContext must be used within the scope of WebmailReloadContextProvider"
        );
    }

    return context;
}
